/**
 * @file useNotificationPreferences.js
 *
 * Manages the logged-in professional's notification matrix — which of THEIR
 * own notifications they receive, per event × per channel (push / email).
 * Never affects patient notifications.
 *
 * Loads the current matrix (or the default all-on matrix while loading), then
 * lets the UI flip a single event/channel. Each toggle is optimistic: it flips
 * local state immediately, PUTs the full matrix, and rolls back + toasts on
 * failure — matching the VisibilityToggle pattern in Configuracion.jsx.
 *
 * @module hooks/useNotificationPreferences
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { notificationPrefsService } from "../services/notificationPrefs";

/**
 * Notification events the professional receives + the channels each supports.
 * Mirrors NOTIFICATION_EVENTS in src/server/services/notificationPrefs.service.js.
 */
export const NOTIFICATION_EVENTS = [
  { key: "appointmentCreated", label: "Cita agendada", channels: ["push", "email"] },
  { key: "appointmentConfirmed", label: "Asistencia confirmada", channels: ["push", "email"] },
  { key: "appointmentCancelled", label: "Cita cancelada", channels: ["push", "email"] },
  { key: "appointmentRescheduled", label: "Cita reagendada", channels: ["push", "email"] },
  { key: "dailyDigest", label: "Resumen diario", channels: ["email"] },
];

/** Channel display labels. */
export const CHANNEL_LABELS = { push: "Push", email: "Email" };

/** Build the default all-on matrix (used while loading, before fetch resolves). */
function defaultMatrix() {
  const m = {};
  for (const { key, channels } of NOTIFICATION_EVENTS) {
    m[key] = {};
    for (const ch of channels) m[key][ch] = true;
  }
  return m;
}

/**
 * Hook for the professional's notification preferences matrix.
 *
 * @returns {{prefs: Object, isLoading: boolean, toggle: Function}}
 */
export function useNotificationPreferences() {
  const [prefs, setPrefs] = useState(defaultMatrix);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPrefs = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await notificationPrefsService.get();
      if (res.data) setPrefs(res.data);
    } catch {
      // Error toast handled by the api interceptor; keep the default matrix.
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  /**
   * Flip one event/channel and persist the full matrix.
   * Optimistic: updates local state first, rolls back on failure.
   *
   * @param {string} event - An event key from NOTIFICATION_EVENTS.
   * @param {string} channel - "push" | "email".
   */
  const toggle = useCallback(async (event, channel) => {
    const previous = prefs;
    const next = {
      ...prefs,
      [event]: { ...prefs[event], [channel]: !prefs[event]?.[channel] },
    };
    setPrefs(next);
    try {
      const res = await notificationPrefsService.update(next);
      if (res.data) setPrefs(res.data);
    } catch {
      setPrefs(previous);
      toast.error("No se pudo guardar");
    }
  }, [prefs]);

  return { prefs, isLoading, toggle };
}
